import { getActiveLanguage } from "./i18n.js";

/**
 * Compact rendering for the aggregate numbers our stats surfaces show: `|v| < 1000` raw, above it
 * three significant digits plus a `k` / `M` / `G` suffix. Andre's reasoning (2026-08-24): our
 * numbers only get this large as aggregates, and there the trailing precision is uninteresting —
 * nobody cares whether it was 14112 or 14135 — while the short form also bounds how wide a cell
 * can get.
 *
 * The magnitude algorithm mirrors the Java half (`infrastructure/web/CompactNumbers.java`, itself
 * a mirror of the tournament CLI's locale-free `CompactNumberFormatter`): HALF_UP rounding, and a
 * once-only re-bucket when that rounding pushes a value onto the next unit (999500 renders
 * `1.00M`, never `1000k`). Two deliberate deviations for UI display:
 *
 *   - The decimal separator follows the active locale — `1,22k` in German, `1.22k` in English. The
 *     dot is German's *thousands* separator, so a dotted `1.22k` reads as 122 there. The suffix
 *     stays `k`/`M`/`G` in both languages (docs/design/COPY.md principle 5 — "Tsd." is exactly the
 *     dusty register that rules out).
 *   - A negative sign is U+2212 MINUS SIGN, not an ASCII hyphen, on the compact and the grouped
 *     form alike so a cell and its tooltip agree typographically.
 *
 * HALF_UP is why the rounding runs on the integer rather than through `toPrecision`: 1005/1000 is
 * 1.00499999999999989 as a double, so `toPrecision(3)` answers `1.00` where the contract wants
 * `1.01`. Rounding the *integer* to three significant digits and only then shifting the decimal
 * point is exact for every value we can be handed (the divisor is always a power of ten).
 *
 * `src/test/resources/compact-number-vectors.json` is the one vector table this module and the
 * Java one are both asserted against, so the two cannot drift on the ties and the unit edges.
 *
 * Locale source is `getActiveLanguage()` — the same active UI language the i18n labels are fetched
 * in, so separators and labels stay in sync including under the footer selector's override
 * (`leaderboard-format.js`'s own reasoning, at length). Built lazily on first format call, then
 * cached: a language change reloads the page, so one locale per page load is correct. Lazy (not a
 * module-load const) so importing this leaf doesn't pull the i18n language API at eval time —
 * which is what lets `charts/charts.js` import it without paying an eager `i18n.js` edge. That
 * transitive edge is free at runtime too: `getActiveLanguage()` reads `localStorage` plus
 * `document.documentElement.lang`, no fetch, no awaited bundle.
 */
let groupedFmt;
let magnitudeFmts;

function formatters() {
  if (groupedFmt == null) {
    const locale = getActiveLanguage();
    groupedFmt = new Intl.NumberFormat(locale);
    // One fixed-fraction formatter per display scale (0/1/2), so the magnitude renders with the
    // locale's decimal separator. `useGrouping: false` is load-bearing: only a value past the top
    // unit (>= 1000G) reaches four magnitude digits, and it must read "5000G" the way the Java
    // side's ungrouped `%f` does.
    magnitudeFmts = [0, 1, 2].map(
      (digits) =>
        new Intl.NumberFormat(locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          useGrouping: false,
        }),
    );
  }
  return { groupedFmt, magnitudeFmts };
}

const MINUS = "−";
const K = 1000;
const M = 1000000;
const G = 1000000000;

// The integer rounded to three significant digits, HALF_UP. `Math.round` is half-away-from-zero,
// and the input is a non-negative integer, so that IS half-up here. The division by a power of ten
// is exact at the tie (the exact quotient k + 0.5 is representable), and every non-tie input sits
// at least 1/pow — orders of magnitude above a double's ulp at this scale — away from one.
// Only ever called past the 1000 floor (`formatCompact` returns before it below that), so `abs`
// always has four digits or more and `pow` is always at least 10.
function roundToThreeSigFigs(abs) {
  const pow = 10 ** (String(abs).length - 3);
  return Math.round(abs / pow) * pow;
}

function pickUnit(abs) {
  if (abs < M) return K;
  if (abs < G) return M;
  return G;
}

function suffixForUnit(unit) {
  return unit === K ? "k" : unit === M ? "M" : "G";
}

function pickDisplayScale(scaled) {
  if (scaled >= 100) return 0;
  if (scaled >= 10) return 1;
  return 2;
}

/**
 * The compact rendering of `value` — raw below 1000, three significant digits plus a unit suffix
 * above it. Negative values keep a U+2212 sign; positives carry no prefix (`formatCompactSigned`
 * below is the form that does).
 */
export function formatCompact(value) {
  const rounded = Math.round(value); // every measure we compact is a whole count or sum
  const abs = Math.abs(rounded);
  const sign = rounded < 0 ? MINUS : "";
  if (abs < K) return sign + abs;

  // Three-sig-fig rounding may push the value onto the next unit (999500 -> 1.00E+3 in "k" ->
  // would print "1000k"). Re-bucket once, unless we're already at the top.
  const sigFigs = roundToThreeSigFigs(abs);
  let unit = pickUnit(abs);
  if (sigFigs / unit >= 1000 && unit < G) unit *= 1000;

  const scaled = sigFigs / unit;
  const digits =
    formatters().magnitudeFmts[pickDisplayScale(scaled)].format(scaled);
  return sign + digits + suffixForUnit(unit);
}

/**
 * `formatCompact` with an explicit sign — `+` for positives, U+2212 for negatives, a bare `0` for
 * zero. The form a signed measure's mark wants (a diverging bar's tip, a Bilanz cell), where the
 * sign is the reading rather than an incidental property.
 */
export function formatCompactSigned(value) {
  const rounded = Math.round(value);
  if (rounded === 0) return "0";
  return rounded > 0 ? "+" + formatCompact(rounded) : formatCompact(rounded);
}

/** The exact value, locale-grouped — `14.112` in German, `14,112` in English. */
export function formatGrouped(value) {
  const rounded = Math.round(value);
  const digits = formatters().groupedFmt.format(Math.abs(rounded));
  return rounded < 0 ? MINUS + digits : digits;
}

/**
 * The exact value for a compacted mark's `data-tooltip-detail` — the mark goes compact, the
 * tooltip keeps the truth. `null` below 1000, where the compact form already IS the exact one and
 * a detail would print the digits the mark already shows.
 */
export function exactDetail(value) {
  if (Math.abs(Math.round(value)) < K) return null;
  return formatGrouped(value);
}
